import { useCallback, useEffect } from "react";
import { z } from "zod";
import { createStorageStore } from "../../../../utils/createStore.ts";
import { useDebugMode } from "../../../components/DebugMode/hooks/useDebugMode.ts";

type ZodObjectWithDefaults = z.ZodObject<{
  [key: string]: z.ZodDefault<z.ZodTypeAny>;
}>;

// FIXME: We should store debug information to unify our debug behavior across the different tools
export const settingsStorageFactory = <TSchema extends ZodObjectWithDefaults>(
  key: string,
  version: number,
  schema: TSchema,
  getDebugDefaultSettings?: () => Partial<z.infer<TSchema>>,
) => {
  const settingsStore = createStorageStore(
    localStorage,
    `${key}-v${version}`,
    z.record(schema.optional()),
    {},
    ({ update }) => ({
      updateSettings: (
        boardUuid: string,
        newSettings: Partial<z.infer<TSchema>>,
      ) => {
        update((draft) => {
          draft[boardUuid] = { ...draft[boardUuid], ...newSettings };
        });
      },
    }),
  );

  const defaultSettings = extractDefaultValues(schema);

  const useSettings = (boardUuid: string) => {
    const settings = settingsStore.useState((it) => it[boardUuid]) ?? {};
    // XXX: Filter out undefined values to prevent them from overriding defaults
    const definedSettings = Object.fromEntries(
      Object.entries(settings).filter(([_, value]) => value !== undefined),
    );

    const isDebugModeOn = useDebugMode();

    useEffect(() => {
      if (getDebugDefaultSettings && !isDebugModeOn) {
        settingsStore.updateSettings(boardUuid, getDebugDefaultSettings());
      }
    }, [isDebugModeOn, boardUuid]);

    return {
      ...defaultSettings,
      ...definedSettings,
    } as z.infer<TSchema>;
  };

  const useSetSettings = (boardUuid: string) =>
    useCallback(
      (newSettings: Partial<z.infer<TSchema>>) => {
        settingsStore.updateSettings(boardUuid, newSettings);
      },
      [boardUuid],
    );

  return { settingsStore, useSettings, useSetSettings };
};

// XXX: We can safely use `schema.parse({})` because typing requires that all fields have a default value. Otherwise, this throws errors for non-optional fields.
const extractDefaultValues = <T extends ZodObjectWithDefaults>(
  schema: T,
): z.infer<T> => schema.parse({});
